import { useMutation, useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import GroupCard from './GroupCard';
import GroupModal from './GroupModal';
import { GroupPolicyAction, useGroupPolicyCheck } from './groupPolicyCheck';
import * as styles from './GroupsView.module.scss';
import PageLayout from '../app/layout/PageLayout';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import EmptyState from '../components/EmptyState';
import PageContent from '../components/PageContent';
import Tooltip from '../components/Tooltip';
import { gql } from '../graphql/generated/gql';
import { UIActions } from '../ui/uiSlice';
import AdminListButton from '../user/AdminListButton';
import { legacyUpdateQueryList } from '../utils/graphql/updateQueryList';

export const GROUPS_VIEW_QUERY = gql(/** @complexity 15 */ `
  query GroupsView {
    session: currentSession {
      organization {
        id
        groups {
          ...GroupCard_group
        }
        settings {
          allowUserGroupCreation
        }
      }
    }
  }
`);

const GROUP_VIEW_CREATE_MUTATION = gql(/** @complexity 12 */ `
  mutation GroupViewCreate($name: String!, $ownerId: ID!) {
    group: createGroup(name: $name, ownerId: $ownerId) {
      ...GroupCard_group
    }
  }
`);

const GroupsView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading } = useQuery(GROUPS_VIEW_QUERY);

  const [createGroupMutation] = useMutation(GROUP_VIEW_CREATE_MUTATION);

  const groups = data?.session.organization?.groups ?? [];
  const isEmpty = !loading && groups.length === 0;
  const canCreate = useGroupPolicyCheck(GroupPolicyAction.CreateGroup);
  const canRequest = useGroupPolicyCheck(GroupPolicyAction.RequestGroup);
  const dispatch = useAppDispatch();
  const showNewGroupModal = () => {
    dispatch(UIActions.openModal(GroupModal));
  };

  useEffect(() => {
    const approveGroupCreation = searchParams.get('approveGroupCreation');
    const ownerId = searchParams.get('ownerId');
    if (canCreate && approveGroupCreation && ownerId) {
      createGroupMutation({
        variables: {
          name: approveGroupCreation,
          ownerId,
        },
        update: legacyUpdateQueryList(
          { query: GROUPS_VIEW_QUERY },
          'session.organization.groups',
          'group',
          true,
        ),
      }).then(() => {
        setSearchParams({});
      });
    }
  }, [canCreate, createGroupMutation, searchParams, setSearchParams]);

  return (
    <PageLayout.Content>
      <PageContent>
        <div className={styles.header}>
          <h1>Groups</h1>
          <div>
            <Tooltip
              content={
                !canCreate && !canRequest
                  ? `Groups can only be\ncreated by ${
                      data?.session.organization.settings
                        ?.allowUserGroupCreation
                        ? 'users'
                        : 'admins'
                    }`
                  : undefined
              }
            >
              <Button
                disabled={!canCreate && !canRequest}
                onClick={showNewGroupModal}
                variant="primary"
              >
                {!canCreate && canRequest ? 'Request group' : 'New group'}
              </Button>
            </Tooltip>
          </div>
        </div>
        {isEmpty ? (
          <div className={styles.emptyStateContainer}>
            <EmptyState
              imageName="posts"
              title="No groups yet..."
              message={
                canCreate
                  ? 'Create a group to add members and topic access.'
                  : 'Ask an admin to create a group for your team.'
              }
              action={
                <>
                  {canCreate && (
                    <Button
                      size="large"
                      variant="secondary"
                      onClick={showNewGroupModal}
                    >
                      Create a group
                    </Button>
                  )}
                  {!canCreate && !canRequest && (
                    <AdminListButton size="large" variant="secondary">
                      See admins
                    </AdminListButton>
                  )}
                </>
              }
            />
          </div>
        ) : (
          <div className={styles.groupCards}>
            {groups.map(
              (group) =>
                group?.id && <GroupCard key={group.id} group={group} />,
            )}
          </div>
        )}
      </PageContent>
    </PageLayout.Content>
  );
};

export default GroupsView;
